import * as React from 'react';
import {useEffect, useState} from 'react';
import Expense, {ExpenseCategory} from "../../../model/Expense";
import {FormLabel, Grid, Typography, useTheme} from "@mui/material";
import {
  dateToLocalFormat,
  humanizeString,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import Model from "../../../model/Model";
import {
  getModelItemIcon,
} from "../../../styles/Icons";
import ModelCard2 from "../../../components/model/ModelCard2";
import ExpenseEditDialog from "./ExpenseEditDialog";
import {isNumber} from "../../../model/ModelUtilities";
import Asset from "../../../model/Asset";
import Liability from "../../../model/Liability";
import Snapshot from "../../../model/Snapshot";

const ExpenseCard = ({
  expense,
  model,
  snapshot,
  projected,
  expanded,
  onChange,
  onSave,
  onDelete,
  onArchive,
  dragHandleProps
}: {
  expense: Expense
  model: Model
  snapshot?: Snapshot
  projected?: number
  expanded?: boolean
  onChange?(event: any, expanded: boolean): void
  onSave?(expense: Expense): void
  onDelete?(expense: Expense): void
  onArchive?(expense: Expense): void
  dragHandleProps?: any
}) => {
  const [expenseModel, setExpenseModel] = useState<Expense>(expense)
  const [description, setDescription] = useState<string>("")
  const [amount, setAmount] = useState<string | undefined>()
  const [amountDate, setAmountDate] = useState<string | undefined>()
  const [amountLock, setAmountLock] = useState<boolean>(false)
  const [projectedAmount, setProjectedAmount] = useState<string | undefined>()
  const [expenseCategory, setExpenseCategory] = useState<string | undefined>()
  const [owner, setOwner] = useState<string | undefined>()
  const [schedule, setSchedule] = useState<string | undefined>()
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [annualInfLock, setAnnualInfLock] = useState<boolean>(false)
  const [discretionary, setDiscretionary] = useState<boolean | undefined>()
  const [asset, setAsset] = useState<Asset | undefined>()
  const [liability, setLiability] = useState<Liability | undefined>()
  const [archived, setArchived] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [saved, setSaved] = useState<boolean>(false)

  useEffect(() => {
    init(expense)
  }, [expense, model, projected])

  const init = (expense: Expense) => {
    setExpenseModel(expense)
    setDescription(`${expense.description}${expense.discretionary ? '*' : ''}`)
    // const itemAmount = modelStore.getBudgetItemAmountDetail(expense, model, snapshot)
    // setAmount(numberToMoneyFormat(itemAmount.amount, 0))
    // setAmountDate((dateToLocalFormat(itemAmount.amountDate)))
    setAmount(numberToMoneyFormat(expense.amount, 0))
    setAmountDate(dateToLocalFormat(expense.amountDate))
    setAmountLock(model.hasLock(expense.id, "amount"))
    setProjectedAmount(projected ? numberToMoneyFormat(projected, 0) : "$0")
    setExpenseCategory(expense.expenseCategory ? humanizeString(ExpenseCategory[expense.expenseCategory]) : undefined)
    setOwner(model.getPerson(expense.ownerId)?.nickname ?? model.getDefaultPersonNickname())
    setSchedule(expense.schedule.getDescription(expense.startDate))
    setStartDate(dateToLocalFormat(expense.startDate))
    setEndDate(dateToLocalFormat(expense.endDate))
    setAnnualInf(numberToPercentFormat(isNumber(expense.annualInf) ? expense.annualInf : model.getDefaultInflationRate("expense"), 2))
    setAnnualInfLock(!isNumber(expense.annualInf) || model.hasLock(expense.id, "annualInf"))
    setDiscretionary(expense.discretionary ?? false)
    const asset = expense.asset ? expense.asset : model.getAssetById(expense.assetId)
    setAsset(asset)
    const liability = expense.liability ? expense.liability : model.getLiabilityById(expense.liabilityId)
    setLiability(liability)
    setArchived(expense.archived)
    setEditOpen(!expense.createdAt)
  }

  const theme = useTheme()

  const styles = {
    heading: {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    },
    row: {
      height: 40,
      minHeight: 40,
      maxHeight: 40,
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    }
  }

  const renderContent = () => {
    let liabilityError = false
    let liabilityDescription
    if (expense.expenseCategory === ExpenseCategory.LoansAndLiabilities) {
      if (liability) {
        if (liability.archived && !expenseModel.archived) {
          liabilityError = true
          liabilityDescription = `${liability.description} (Archived)`
        } else {
          liabilityDescription = liability.description
        }
      } else if (expenseModel.liabilityId) {
        liabilityError = true
        liabilityDescription = "Not found"
      } else {
        liabilityError = true
        liabilityDescription = "Not set"
      }
    }

    let paymentError = false
    let paymentDescription
    if (expenseModel.assetId) {
      if (asset) {
        if (asset.archived && !expenseModel.archived) {
          paymentError = true
          paymentDescription = `${asset.description} (Archived)`
        } else {
          paymentDescription = asset.description
        }
      } else {
        paymentError = true
        paymentDescription = "Not found"
      }
    }

    return (
      <Grid container direction="column" spacing={0}>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Amount</FormLabel>
              <Typography variant="body2" color={amountLock ? "text.secondary" : "inherit"}>
                {amount}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Amount Date</FormLabel>
              <Typography variant="body2">
                {amountDate}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <FormLabel sx={styles.formLabel}>Frequency</FormLabel>
          <Typography variant="body2">
            {schedule}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Owner</FormLabel>
              <Typography variant="body2">
                {owner}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <FormLabel sx={styles.formLabel}>Type</FormLabel>
              <Typography variant="body2">
                {discretionary ? "Discretionary" : "Essential"}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Start Date</FormLabel>
              <Typography variant="body2">
                {startDate}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>End Date</FormLabel>
              <Typography variant="body2" color={expense.end ? "inherit" : "text.secondary"}>
                {endDate}
              </Typography>
            </Grid>
            {expense.expenseCategory !== ExpenseCategory.LoansAndLiabilities &&
              <Grid item xs={4}>
                <FormLabel sx={styles.formLabel}>Annual Inflation %</FormLabel>
                <Typography variant="body2" color={annualInfLock ? "text.secondary" : "inherit"}>
                  {annualInf}
                </Typography>
              </Grid>
            }
          </Grid>
        </Grid>
        {expense.assetId &&
          <Grid item xs={12} sx={styles.row}>
            <FormLabel sx={styles.formLabel}>Payment Account</FormLabel>
            <Typography variant="body2" color={paymentError ? "error" : undefined}>
              {paymentDescription}
            </Typography>
          </Grid>
        }
        {(liability || expense.expenseCategory === ExpenseCategory.LoansAndLiabilities) &&
          <Grid item xs={12} sx={styles.row}>
            <FormLabel sx={styles.formLabel}>Liability Account</FormLabel>
            <Typography variant="body2" color={liabilityError ? "error" : undefined}>
              {liabilityDescription}
            </Typography>
          </Grid>
        }
      </Grid>
    )
  }

  const icon = getModelItemIcon(expense)
  // let iconColor = globalColors.expenseCategory[expense.expenseCategory]
  // let icon
  // switch (expense.expenseCategory  ?? ExpenseCategory.GivingAndMisc) {
  //   case ExpenseCategory.Housing:
  //     icon = <HousingExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   case ExpenseCategory.LoansAndLiabilities:
  //     icon = <LoanExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   case ExpenseCategory.FoodAndPersonalCare:
  //     icon = <FoodAndPersonalExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   case ExpenseCategory.InsuranceAndMedical:
  //     icon = <InsuranceAndMedicalExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   case ExpenseCategory.VehiclesAndTransportation:
  //     icon = <VehiclesAndTransporationExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   case ExpenseCategory.TravelAndEntertainment:
  //     icon = <TravelAndEntertainmentExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   case ExpenseCategory.GivingAndMisc:
  //     icon = <GivingAndMiscExpenseIcon backgroundColor={iconColor}/>
  //     break
  //   default:
  //     icon = <DiscretionaryExpenseIcon/>
  //     break
  // }

  return (
    <React.Fragment>
      <ModelCard2
        icon={icon}
        title={description}
        value={projectedAmount}
        valueColor="#881E10"
        archived={archived}
        expanded={expanded}
        onChange={onChange}
        editButtonId={`edit-btn-${expenseModel.id}`}
        onEdit={(event: any) => {
          setEditOpen(!editOpen)
        }}
        dragHandleProps={dragHandleProps}
      >
        {renderContent()}
      </ModelCard2>
      {editOpen &&
        <ExpenseEditDialog expense={expenseModel} model={model} snapshot={snapshot}
                           open={editOpen}
                           onSave={(update: Expense) => {
                             init(update)
                             setEditOpen(false)
                             if (onSave) {
                               onSave(update)
                               setSaved(true)
                             }
                           }}
                           onDelete={(deleted: Expense) => {
                             setEditOpen(false)
                             if (onDelete) {
                               onDelete(deleted)
                             }
                           }}
                           onArchive={(expense: Expense) => {
                             setEditOpen(false)
                             if (onArchive) {
                               onArchive(expense)
                             }
                           }}
                           onClose={() => {
                             setEditOpen(false)
                             if (!expenseModel.createdAt && onDelete) {
                               onDelete(expenseModel)
                             }
                           }}/>
      }
    </React.Fragment>
  )
}

export default ExpenseCard